import { Injectable, computed, effect, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { convertObjectArrayToString } from 'shared/util/convert-object-array-to-string';
import {
    VisualPermissionCreateInputGraph,
    VisualPermissionKeysEnum,
    VisualPermissionResponse,
    VisualPermissionUpdateInputGraph,
    VisualPermissionsAccessTypeEnum,
    VisualPermissionsEnum,
} from 'users/interfaces/visual-permissions.interface';

type PermissionsRecord = Record<VisualPermissionKeysEnum, boolean>;

@Injectable({
    providedIn: 'root',
})
export class VisualsPermissionsService {
    private permissionChangeSignal = toSignal(this.ngxPermissionService.permissions$);
    private usersApollo: ApolloBase;

    private readonly permissionsList = Object.values(VisualPermissionKeysEnum);

    public readonly accessTypeHierarchy: VisualPermissionsAccessTypeEnum[] = [
        VisualPermissionsAccessTypeEnum.VIEW,
        VisualPermissionsAccessTypeEnum.EDIT,
        VisualPermissionsAccessTypeEnum.ACTIVE,
    ];

    private permissions = signal<PermissionsRecord>(this.createInitialPermissions());
    permissionsSet = signal<boolean>(false);

    closeSupplierUsersModal = signal<boolean>(false);

    visualsSectionActive = signal<boolean>(false);

    notAllowedVisualPermissionChanges = signal<VisualPermissionsEnum[]>([]);

    saveCompleted = new Subject<boolean>();

    createUserGroupVisualPermissionsId = signal<string | null>(null);

    permissionsEnum = VisualPermissionKeysEnum;

    userId = signal<string | null>(null);
    userId$ = toObservable(this.userId);

    userGroupId = signal<string | null>(null);
    userGroupId$ = toObservable(this.userGroupId);

    loadDefaultPermissions = signal<boolean>(false);
    loadDefaultPermissions$ = toObservable(this.loadDefaultPermissions);
    isOnCreatePermissionsFlow = signal<boolean>(false);
    isOnCreatePermissionsFlow$ = toObservable(this.isOnCreatePermissionsFlow);

    // Individual permission computed signals
    canViewAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.ASSETS_METADATA_VIEW));
    canEditAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.ASSETS_METADATA_EDIT));
    canCreateAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.CREATE_ASSETS_ACTIVE));
    canDeleteAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.DELETE_ASSETS_ACTIVE));
    canEnableDisableAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.ENABLE_DISABLE_ASSETS_ACTIVE));
    canReorderAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.REORDER_ASSETS_ACTIVE));
    canCropAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.CROP_ASSETS_ACTIVE));
    canDownloadAssets = computed(() => this.hasPermission(VisualPermissionKeysEnum.DOWNLOAD_ASSETS_ACTIVE));
    canViewRoomTypes = computed(() => this.hasPermission(VisualPermissionKeysEnum.ROOM_TYPES_VIEW));
    canEditRoomTypes = computed(() => this.hasPermission(VisualPermissionKeysEnum.ROOM_TYPES_EDIT));
    canViewMeetingRoomTypes = computed(() => this.hasPermission(VisualPermissionKeysEnum.MEETING_ROOM_TYPES_VIEW));
    canEditMeetingRoomTypes = computed(() => this.hasPermission(VisualPermissionKeysEnum.MEETING_ROOM_TYPES_EDIT));
    canViewPhotoOrganizer = computed(() => this.hasPermission(VisualPermissionKeysEnum.PHOTO_ORGANIZER_VIEW));
    canEditPhotoOrganizer = computed(() => this.hasPermission(VisualPermissionKeysEnum.PHOTO_ORGANIZER_EDIT));
    canViewCustomContent = computed(() => this.hasPermission(VisualPermissionKeysEnum.CUSTOM_CONTENT_VIEW));
    canEditCustomContent = computed(() => this.hasPermission(VisualPermissionKeysEnum.CUSTOM_CONTENT_EDIT));

    loggedInUserHasVisualsUIEnabled = signal<boolean | null>(null);

    hasVisualsViewPermissions = computed(() => {
        if (!this.permissionsSet()) {
            return null;
        }
        return this.canViewAssets() || this.canViewRoomTypes() || this.canViewMeetingRoomTypes() || this.canViewPhotoOrganizer();
    });

    constructor(
        private ngxPermissionService: NgxPermissionsService,
        private apolloProvider: Apollo,
    ) {
        effect(() => {
            this.permissionChangeSignal();
            this.updatePermissions();
        });
        this.usersApollo = this.apolloProvider.use('usersAPI');
    }

    defaultPermissions = toSignal(
        combineLatest([this.userId$, this.userGroupId$, this.loadDefaultPermissions$, this.isOnCreatePermissionsFlow$]).pipe(
            switchMap(
                ([userId, userGroupId, loadDefaultPermissions, isOnCreatePermissionsFlow]): Observable<VisualPermissionResponse[]> => {
                    if (!loadDefaultPermissions || (!userId && !userGroupId && !isOnCreatePermissionsFlow)) {
                        return of([]);
                    }

                    return this.getDefaultPermissions(userId, userGroupId).pipe(
                        map((response) => response.data || []),
                        catchError(() => of([])),
                    );
                },
            ),
        ),
        { initialValue: [] as VisualPermissionResponse[] },
    );

    private getDefaultPermissions(userId: string | null = null, userGroupId: string | null = null) {
        const variables: { userId?: string; userGroupId?: string } = {};
        if (userId) {
            variables.userId = userId;
        }
        if (userGroupId) {
            variables.userGroupId = userGroupId;
        }

        return this.usersApollo
            .query({
                query: gql`
                    query getDefaultPermissionsForTarget($userId: Guid, $userGroupId: Guid) {
                        visualPermissions {
                            getDefaultPermissionsForTarget(userId: $userId, userGroupId: $userGroupId) {
                                data {
                                    userId
                                    userGroupId
                                    type
                                    accessType
                                    isImplemented
                                    derivedVisualPermissions {
                                        visualPermissionId
                                        type
                                        isActive
                                        isImplemented
                                        id
                                    }
                                    id
                                }
                                totalCount
                            }
                        }
                    }
                `,
                variables,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.visualPermissions.getDefaultPermissionsForTarget;
                    return { data: returnValue.data, totalCount: returnValue.totalCount };
                }),
            );
    }

    private hasPermission(permission: VisualPermissionKeysEnum): boolean {
        return this.permissionsSet() && this.permissions()[permission];
    }

    private async updatePermissions(): Promise<void> {
        try {
            const permissionResults = await Promise.all(
                this.permissionsList.map((permission) => this.ngxPermissionService.hasPermission(permission)),
            );

            const newPermissions = this.permissionsList.reduce(
                (acc, permission, index) => ({
                    ...acc,
                    [permission]: permissionResults[index],
                }),
                {} as PermissionsRecord,
            );

            this.permissions.set(newPermissions);
        } catch (error) {
            console.error('Error checking permissions:', error);
            this.permissions.set(this.createInitialPermissions());
        }
    }

    setPermissions(permissions: VisualPermissionResponse[]) {
        const permissionsToSet: VisualPermissionKeysEnum[] = [];

        permissions.forEach((permission) => {
            if (!permission.isImplemented) return;

            const permissionKey = `${permission.type}_${permission.accessType}`;
            if (this.permissionsList.includes(permissionKey as VisualPermissionKeysEnum)) {
                permissionsToSet.push(permissionKey as VisualPermissionKeysEnum);

                this.addHierarchicalPermissions(permission.type, permission.accessType, permissionsToSet);
            }

            if (permission.derivedVisualPermissions) {
                permission.derivedVisualPermissions.forEach((derivedPermission) => {
                    if (derivedPermission.isImplemented && derivedPermission.isActive) {
                        const derivedPermissionKey = `${derivedPermission.type}_${VisualPermissionsAccessTypeEnum.ACTIVE}`;
                        if (this.permissionsList.includes(derivedPermissionKey as VisualPermissionKeysEnum)) {
                            permissionsToSet.push(derivedPermissionKey as VisualPermissionKeysEnum);
                        }
                    }
                });
            }
        });

        this.ngxPermissionService.loadPermissions(permissionsToSet);
        this.permissionsSet.set(true);
    }

    private addHierarchicalPermissions(
        permissionType: string,
        accessType: VisualPermissionsAccessTypeEnum,
        permissionsToSet: VisualPermissionKeysEnum[],
    ): void {
        const currentAccessTypeIndex = this.accessTypeHierarchy.indexOf(accessType);

        if (currentAccessTypeIndex === -1) return;

        for (let accessTypeIndex = 0; accessTypeIndex < currentAccessTypeIndex; accessTypeIndex++) {
            const lowerAccessType = this.accessTypeHierarchy[accessTypeIndex];
            const lowerPermissionKey = `${permissionType}_${lowerAccessType}` as VisualPermissionKeysEnum;

            if (this.permissionsList.includes(lowerPermissionKey) && !permissionsToSet.includes(lowerPermissionKey)) {
                permissionsToSet.push(lowerPermissionKey);
            }
        }
    }

    setAdminPermissions() {
        this.ngxPermissionService.loadPermissions(this.permissionsList);
        this.permissionsSet.set(true);
    }

    private createInitialPermissions(): PermissionsRecord {
        return this.permissionsList.reduce((acc, permission) => ({ ...acc, [permission]: false }), {} as PermissionsRecord);
    }

    private processPermissionPayload(permissions: VisualPermissionUpdateInputGraph[]): string {
        const flattenedPermissions = permissions.map((permission) => {
            const flattened: any = {
                id: permission.id,
                type: permission.type,
                accessType: permission.accessType,
            };

            if (permission.userId) {
                flattened.userId = permission.userId;
            }
            if (permission.userGroupId) {
                flattened.userGroupId = permission.userGroupId;
            }

            if (permission.updatedDerivedVisualPermissions && permission.updatedDerivedVisualPermissions.length > 0) {
                const derivedString = permission.updatedDerivedVisualPermissions
                    .map((derived) => `{ id: "${derived.id}", isActive: ${derived.isActive}, type: ${derived.type} }`)
                    .join(', ');
                flattened.updatedDerivedVisualPermissions = `[${derivedString}]`;
            }

            return flattened;
        });

        const updateParameters = convertObjectArrayToString(
            flattenedPermissions,
            ['id', 'userId', 'userGroupId', 'accessType', 'updatedDerivedVisualPermissions', 'type'],
            ['accessType', 'updatedDerivedVisualPermissions', 'type'],
        );

        return updateParameters;
    }

    private processCreatePermissionPayload(permissions: VisualPermissionCreateInputGraph[]): string {
        const flattenedPermissions = permissions.map((permission) => {
            const flattened: any = {
                type: permission.type,
                accessType: permission.accessType,
            };

            if (permission.userId) {
                flattened.userId = permission.userId;
            }
            if (permission.userGroupId) {
                flattened.userGroupId = permission.userGroupId;
            }
            if (permission.updatedDerivedVisualPermissions && permission.updatedDerivedVisualPermissions.length > 0) {
                const derivedString = permission.updatedDerivedVisualPermissions
                    .map((derived) => `{ isActive: ${derived.isActive}, type: ${derived.type} }`)
                    .join(', ');
                flattened.updatedDerivedVisualPermissions = `[${derivedString}]`;
            }
            return flattened;
        });
        const createParameters = convertObjectArrayToString(
            flattenedPermissions,
            ['userId', 'userGroupId', 'accessType', 'updatedDerivedVisualPermissions', 'type'],
            ['accessType', 'updatedDerivedVisualPermissions', 'type'],
        );
        return createParameters;
    }

    updateUserPermissions(permissions: VisualPermissionUpdateInputGraph[]) {
        const updateParameters = this.processPermissionPayload(permissions);

        const updateUserPermissions = gql`
        mutation UpdateMultiplePermissions {
            visualPermissions {
                updateMultiplePermissions(visualPermissions: ${updateParameters}) {
                    data
                    statusCode
                    message
                }
            }
        }
    `;

        return this.usersApollo.mutate({
            mutation: updateUserPermissions,
        });
    }

    updateGroupPermissions(permissions: VisualPermissionUpdateInputGraph[]) {
        const updateParameters = this.processPermissionPayload(permissions);

        const updateUserGroupPermissions = gql`
        mutation UpdateMultipleGroupPermissions {
            visualPermissions {
                updateMultipleGroupPermissions(visualPermissions: ${updateParameters}) {
                    data
                    statusCode
                    message
                }
            }
        }
    `;

        return this.usersApollo.mutate({
            mutation: updateUserGroupPermissions,
        });
    }

    createUserPermissions(permissions: VisualPermissionCreateInputGraph[]) {
        const createParameters = this.processCreatePermissionPayload(permissions);

        const createUserPermissions = gql`
        mutation CreateMultiplePermissions {
            visualPermissions {
                createMultiplePermissions(visualPermissions: ${createParameters}) {
                    data
                    statusCode
                    message
                }
    }
        }
    `;

        return this.usersApollo.mutate({
            mutation: createUserPermissions,
        });
    }

    createGroupPermissions(permissions: VisualPermissionCreateInputGraph[]) {
        const createParameters = this.processCreatePermissionPayload(permissions);

        const createUserGroupPermissions = gql`
        mutation CreateMultipleGroupPermissions {
            visualPermissions {
                createMultipleGroupPermissions(visualPermissions: ${createParameters}) {
                    data
                    statusCode
                    message
                }
            }
    }
        `;

        return this.usersApollo.mutate({
            mutation: createUserGroupPermissions,
        });
    }
}
